import styled from 'styled-components';
import Container from '../../../common/components/atoms/Container';
import { lightenHex } from '../utils/annotationPresentation';

export const ListOuterWrapper = styled.div`
  width: 100%;
  padding: 24px 32px 32px;
  background: transparent;

  ${props => props.theme.media.tablet`
    padding: 16px 20px 24px;
  `}
`;

export const ListContainer = styled(Container)`
  background-color: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.greyL};
  border-radius: ${props => props.theme.utils.borderRadius};
  padding: 24px;
  gap: 16px;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  max-height: 60vh; /* restore internal scroll behavior */
  overflow: hidden; /* contain inner scroll */

  ${props => props.theme.media.tablet`
    padding: 16px;
  `}
`;

export const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.greyL};
  margin-bottom: 16px;
`;

export const TitleSection = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 160px;
`;

export const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Petasense Open Sans';
  color: ${props => props.theme.colors.greyXXD};
`;

export const Count = styled.span`
  color: ${props => props.theme.colors.greyXD};
  font-size: 13px;
  font-weight: 600;
`;

export const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1 1 260px;
  gap: 12px;
  flex-wrap: wrap;
`;

export const SearchContainer = styled.div`
  flex: 1 1 240px;
  min-width: 220px;
  max-width: 420px;
`;

export const SortWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

export const SortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  min-height: 36px;
  font-size: 13px;
  font-weight: 600;
  background-color: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.grey};
  border-radius: ${props => props.theme.utils.borderRadius};
  color: ${props => props.theme.colors.greyXXD};
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  white-space: nowrap;

  &:hover,
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.borderGrayHover};
    background-color: ${props => props.theme.colors.greyXXXL};
    color: ${props => props.theme.colors.black};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${props => props.theme.colors.borderActive};
  }
`;

export const SortIcon = styled.span`
  font-size: 14px;
  color: ${props => props.theme.colors.greyXD};
`;

export const SortCaret = styled.span`
  font-size: 11px;
  color: ${props => props.theme.colors.greyXD};
`;

export const SortMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 200px;
  background: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.greyL};
  border-radius: ${props => props.theme.utils.borderRadius};
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  padding: 8px 0;
  z-index: 10;
`;

export const SortMenuItem = styled.button`
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  border: none;
  text-align: left;
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.theme.colors.greyXXD};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover,
  &:focus {
    outline: none;
    background-color: ${props => props.theme.colors.greyXXXL};
  }
`;

export const AnnotationsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1 1 auto;
  min-height: 0; /* enable proper overflow within flex parent */
  overflow-y: auto;
  overscroll-behavior: contain;
`;

export const AnnotationCard = styled.div`
  display: flex;
  padding: 16px;
  background-color: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.greyL};
  border-radius: ${props => props.theme.utils.borderRadius};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  cursor: pointer;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: ${props => props.theme.colors.grey};
  }
`;

export const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.greyXXXL};
  border: 2px solid ${props => (props.color ? lightenHex(props.color, 48) : props.theme.colors.grey)};
  color: ${props => props.theme.colors.greyXXD};
  font-size: 20px;
  margin-right: 16px;
  flex-shrink: 0;
`;

export const ContentSection = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ExternalLinkIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.greyXXXL};
  border: 1px solid ${props => props.theme.colors.grey};
  color: ${props => props.theme.colors.greyXD};
  font-size: 12px;
  margin-left: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.colors.greyXXXL};
    border-color: ${props => props.theme.colors.borderGrayHover};
    color: ${props => props.theme.colors.black};
  }
`;

export const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

export const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border: 1px solid ${props => (props.color ? lightenHex(props.color, 48) : props.theme.colors.grey)};
  background-color: ${props => props.theme.colors.greyXXXL};
  color: ${props => (props.color ? lightenHex(props.color, 48) : props.theme.colors.greyXXD)};
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

export const SeverityBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: ${(props) => {
    const s = (props.severity || '').toLowerCase();
    if (s === 'critical') return lightenHex(props.theme.colors.critical, 120);
    if (s === 'high' || s === 'warning' || s === 'poor') return lightenHex(props.theme.colors.warning, 120);
    if (s === 'medium' || s === 'fair') return lightenHex(props.theme.colors.moderate, 120);
    if (s === 'low' || s === 'good') return props.theme.colors.greenXXL;
    return props.theme.colors.greyXXXL;
  }};
  color: ${(props) => {
    const s = (props.severity || '').toLowerCase();
    if (s === 'critical') return props.theme.colors.critical;
    if (s === 'high' || s === 'warning' || s === 'poor') return props.theme.colors.warning;
    if (s === 'medium' || s === 'fair') return props.theme.colors.moderate;
    if (s === 'low' || s === 'good') return props.theme.colors.normal;
    return props.theme.colors.greyXXD;
  }};
`;

export const Description = styled.div`
  font-size: 14px;
  line-height: 1.4;
  color: ${props => props.theme.colors.greyXXD};
  margin-bottom: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const TimeInfo = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.greyXD};
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  color: ${props => props.theme.colors.greyXD};
`;

export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
`;
